import { useCallback, useMemo } from 'react';
import { useApiClient } from './useApiClient';
import { DEFAULT_API_OPTIONS } from './defaults';

const NOOP = () => {};

const createMissingMethod = (methodName) => () => {
	throw new Error(`useApi client is missing method: ${methodName}`);
};

const DEFAULT_API_CLIENT = {
	get: createMissingMethod('get'),
	getBlob: createMissingMethod('getBlob'),
	post: createMissingMethod('post'),
	put: createMissingMethod('put'),
	patch: createMissingMethod('patch'),
	del: createMissingMethod('del'),
	postBlob: createMissingMethod('postBlob'),
	cancel: NOOP,
};

/**
 * Advanced API hook for transport-level access.
 *
 * Prefer specialized API hooks in feature components (for example,
 * useOrdersApi/useProductsApi/useContactApi).
 *
 * Use this hook directly when you need low-level access to:
 * - call(apiDefinition)
 * - direct get/post/put/patch/del/postBlob methods
 * - shared cancel behavior from the API client
 */
export const useApi = (baseOptions = DEFAULT_API_OPTIONS) => {
	const apiClient = useApiClient(baseOptions);
	const api = useMemo(
		() => ({
			...DEFAULT_API_CLIENT,
			...(apiClient ?? {}),
		}),
		[apiClient]
	);

	// Convenient .call() method for using domain API definitions
	const call = useCallback(
		async (apiDefinition) => {
			if (!apiDefinition?.url) {
				throw new Error('apiDefinition must have a "url" property');
			}

			const {
				url,
				method = 'post',
				payload = null,
				options = {},
				abortController = null,
			} = apiDefinition;

			switch (method.toLowerCase()) {
				case 'get':
					return api.get(url, abortController);

				case 'getblob':
					return api.getBlob(url, abortController);

				case 'post':
					return api.post(url, payload, options, abortController);

				case 'postblob':
					return api.postBlob(url, payload, abortController);

				case 'put':
					return api.put(url, payload, abortController);

				case 'patch':
					return api.patch(url, payload, abortController);

				case 'del':
				case 'delete':
					return api.del(url, abortController);

				default:
					throw new Error(`Unsupported method: ${method}`);
			}
		},
		[api]
	);

	return useMemo(
		() => ({
			...api, // allows direct calls: api.get(), api.postBlob(), etc.
			call, // allows domain definition calls: api.call(orderApi.getOrderList(params))
		}),
		[api, call]
	);
};
